/**
 * useSkus — TanStack Query hook that fetches the full SKU catalog.
 *
 * Endpoint: GET /api/v1/skus
 * Response envelope: { skus: Sku[] }
 *
 * Returns the unwrapped array so callers never touch the envelope.
 * JWT is required for all authenticated API calls; if not yet present
 * the query is suspended (enabled: false) — auth gate upstream ensures
 * the user is authenticated before the catalog page is reachable.
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';
import type { Sku } from '../types/sku';

interface SkusEnvelope {
  skus: Sku[];
}

export function useSkus(): {
  data: Sku[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const jwt = useAuth((s) => s.jwt);

  const query = useQuery<Sku[], Error>({
    queryKey: ['skus'],
    queryFn: async () => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch('/api/v1/skus', jwt);
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`skus_fetch_failed: ${res.status} ${body}`);
      }
      const envelope = (await res.json()) as SkusEnvelope;
      return envelope.skus;
    },
    enabled: !!jwt,
    staleTime: 60_000, // catalog data is stable; 1 min before background refetch
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
